// oxlint-disable no-empty-function, no-unused-vars
// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import _ from 'underscore';

import type {Assembly, AssemblySlots} from 'assembly/AssemblyModels';
import type {Sequence as BioJSSequence} from 'BioJS';
import type {Model as FragmentResult} from 'biojs/FragmentResult';
import type {Collection as SequencePrimers, Model as BioJSSequencePrimer} from 'biojs/SequencePrimer';
import type {Sequence} from 'components/Sequence';
import type {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import type {Selection} from 'molbio/maps/selectables/Selection';
import type SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import type {BufferState} from 'Navigation';

// The forward and reverse primers flanking a fragment result, either of which may be absent.
export type FlankingPrimers = [BioJSSequencePrimer | null, BioJSSequencePrimer | null];

// [3' end of the upstream fragment, 5' start of the downstream fragment, downstream forward primer,
// upstream reverse primer]
export type Junction = [number, number, BioJSSequencePrimer | null, BioJSSequencePrimer | null];

function getFlankingPrimers(sequence: BioJSSequence, fragmentResult: FragmentResult): FlankingPrimers {
  if (!fragmentResult.primerFlanked()) {
    return [null, null];
  }
  const pair = sequence.primerPairs().get(fragmentResult.primerPairID()!);
  // Even though primerFlanked should return false if the primer pair is detached, in certain codepaths
  // (e.g. detaching a single primer with the assembly tab open) the primerPairs() collection gets updated
  // sans primer pair while the fragment's client-side model still thinks it has a primer pair.
  // We handle this by just returning early.
  if (pair == null) {
    return [null, null];
  }
  return pair.getPrimers(sequence.sequencePrimers());
}

function getJunctions(sequence: BioJSSequence): Array<Junction> {
  const fragmentResults = sequence.fragmentResults();
  return fragmentResults.map((fragmentResult, i): Junction => {
    const nextFragmentResult = fragmentResults.at((i + 1) % fragmentResults.length);
    const [_fwd, rev] = Array.from(getFlankingPrimers(sequence, fragmentResult));
    const [nextFwd, _nextRev] = Array.from(getFlankingPrimers(sequence, nextFragmentResult));
    return [fragmentResult.end()!, nextFragmentResult.start()!, nextFwd, rev];
  });
}

function validSpacerOrder(slots: AssemblySlots): boolean {
  const usesEnzymeArray = slots.map((slot) => !slot.isEmpty() && slot.singleFragment().enzymeIDs() != null);
  for (let i = 0; i < slots.models.length; i++) {
    const slot = slots.models[i];
    if (!slot.isSpacer()) {
      continue;
    }
    const prevIndex = i === 0 ? slots.length - 1 : i - 1;
    const nextIndex = (i + 1) % slots.length;
    if (usesEnzymeArray[prevIndex] && usesEnzymeArray[nextIndex]) {
      // Spacer slot cannot be between two digest-cut fragments.
      return false;
    }
  }
  return true;
}

function addPrimerNameSuffixes(sequencePrimers: SequencePrimers): void {
  const totalPrimerNameCounts = _.countBy(sequencePrimers.models, (primer) => primer.name());
  const currentPrimerNameCounts: Record<string, number> = {};
  sequencePrimers.each((primer) => {
    const primerName = primer.name();
    if (totalPrimerNameCounts[primerName] < 2) {
      return;
    }
    // Add numerical suffix to deduplicate primer names.
    // eslint-disable-next-line benchlint/no-in-operator
    if (!(primerName in currentPrimerNameCounts)) {
      currentPrimerNameCounts[primerName] = 0;
    }
    currentPrimerNameCounts[primerName]++;
    primer.set({name: `${primerName} (${currentPrimerNameCounts[primerName]})`});
  });
}

// Every method below is a no-op or unconditional default that ignores its arguments. Some parameter
// lists are stale relative to how `AssemblyPlugin` callers actually invoke them, so those parameters
// are typed `unknown` rather than reshaped.
const basePlugin = {
  assemblyType: null as ASSEMBLY_TYPE | null,

  attachTabs(sequence: Sequence, buffer: BufferState): void {},

  // Returns {warnings=[], errors=[]} for assembly
  checkAssembly(assembly: Assembly): never {
    throw new Error(`Did not expect checkAssembly to be called on basePlugin - \
make sure it's been implemented`);
  },

  // Based on the sequence parents, returns an array of [color, 5' start, 3' start] to color the
  // sequence regions, with one per parent.
  getFragmentResultPieces(sequence: BioJSSequence): Array<[string, number, number]> {
    return sequence
      .fragmentResults()
      .map((fragmentResult) => [
        fragmentResult.color(),
        fragmentResult.start(),
        fragmentResult.end(),
      ]) as Array<[string, number, number]>;
  },

  isFlankingPrimer(sequence: BioJSSequence, primer: BioJSSequencePrimer | SequencePrimer): boolean {
    if (sequence.finalized()) {
      return false;
    }
    // @ts-expect-error Left over from bulk-decaffeinate; retained so the getter call order is unchanged.
    const primerPairs = sequence.primerPairs();
    // @ts-expect-error Left over from bulk-decaffeinate; retained so the getter call order is unchanged.
    const primers = sequence.sequencePrimers();
    return (
      sequence
        .fragmentResults()
        .chain()
        .map((fragmentResult: FragmentResult) => {
          if (fragmentResult.primerFlanked()) {
            return getFlankingPrimers(sequence, fragmentResult);
          }
          return [];
        })
        .flatten()
        .compact()
        .find((flankingPrimer: BioJSSequencePrimer) => flankingPrimer.id === primer.id)
        .value() != null
    );
  },

  canDelete(sequence: BioJSSequence, selection: Selection, errback: (message: string) => void): boolean {
    return true;
  },

  // Determines whether or not we can insert at a selection (if no range is selected)
  // or initiate a replace at that selection (if a range is selected). We say "initiate" because
  // `canReplace` can still reject the change.
  canEditSelection(
    sequence: BioJSSequence,
    selection: Selection,
    errback: (message: string) => void
  ): boolean {
    return true;
  },

  // Checks whether the current selection can be replaced by specified bases.
  canReplace(sequence: BioJSSequence, selection: Selection, errback: unknown): boolean {
    return true;
  },

  didDelete(
    sequence: BioJSSequence,
    selection: Selection,
    bases: unknown,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {},

  didInsert(
    sequence: BioJSSequence,
    selection: unknown,
    bases: string,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {},

  didReplace(
    sequence: BioJSSequence,
    selection: Selection,
    bases: string,
    callback: (message: string) => void,
    errback: (message: string) => void
  ): void {},

  shouldOpenSequenceFiles(): boolean {
    return true;
  },
};

export {addPrimerNameSuffixes, basePlugin, getFlankingPrimers, getJunctions, validSpacerOrder};
